import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Brain } from "lucide-react";
import InsightCard from "./InsightCard";

export default function CountdownInsights({
  items,
  startTime = 10, // short countdown
  previewTime = 21 * 24 * 3600, // simulate weeks (in seconds)
  showRealInsights = false, // toggle when insights are ready
}) {
  const [timeLeft, setTimeLeft] = useState(startTime);
  const [showPreview, setShowPreview] = useState(false);
  const [showInsights, setShowInsights] = useState(false);

  const insights = items || [
    "Top channels driving engagement",
    "Peak publishing times",
    "Content themes trending upward",
    "Audience segments with highest retention",
  ];

  useEffect(() => {
    if (showRealInsights) {
      setShowInsights(true);
      return;
    }

    if (timeLeft === 0 && !showPreview) {
      setShowPreview(true);
      setTimeLeft(previewTime);
      return;
    }

    if (timeLeft === 0 && showPreview) {
      return;
    }

    const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, showPreview, showRealInsights, previewTime]);

  return (
    <div className="relative flex items-center justify-center h-[500px] w-full bg-gray-950 text-white overflow-hidden rounded-xl mb-8">
      {/* animated pulsing background */}
      <div className="absolute inset-0 bg-gradient-to-tr from-purple-800 via-indigo-900 to-black animate-pulse-slow blur-2xl opacity-60 rounded-xl" />

      <div className="relative z-10 flex flex-col items-center text-center space-y-6 px-4 w-full max-w-xl">
        {!showPreview && !showInsights ? (
          <>
            <motion.h1
              className="text-2xl md:text-3xl font-bold"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              We’re building your data...
            </motion.h1>

            <motion.div
              key={timeLeft}
              className="text-6xl md:text-7xl font-mono font-bold text-purple-400 drop-shadow-lg"
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {timeLeft}
            </motion.div>

            <motion.span
              className="flex items-center gap-2 text-purple-300 font-medium tracking-wide"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <Brain className="w-5 h-5 text-purple-400" />
              Powered by web3dma
            </motion.span>
          </>
        ) : showPreview && !showInsights ? (
          // Preview state: show blurred insights
          <motion.div className="space-y-4 w-full">
            <h2 className="text-xl md:text-2xl font-semibold text-purple-300 flex items-center justify-center gap-2">
              <Brain className="w-6 h-6 text-purple-400" />
              Coming Soon
            </h2>
            <p>
              We're collecting some data and building your insights. Check back
              soon!
            </p>
            <ul className="space-y-3">
              {insights.map((_, idx) => (
                <motion.li
                  key={idx}
                  className="flex items-center gap-3 bg-white/10 px-6 py-4 rounded-3xl shadow-md backdrop-blur-sm animate-pulse"
                >
                  <Brain className="w-5 h-5 text-purple-400 opacity-50" />
                  <span className="w-full h-4 bg-white/20 rounded-md"></span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        ) : (
          // Actual insights ready
          <AnimatePresence>
            <motion.div
              className="space-y-4 w-full"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-xl md:text-2xl font-semibold text-purple-300 flex items-center justify-center gap-2">
                <Brain className="w-6 h-6 text-purple-400" />
                Intelligent Insights Ready
              </h2>
              <ul className="space-y-3">
                {insights.map((insight, idx) => (
                  <InsightCard insight={insight} idx={idx} />
                ))}
              </ul>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
